import React, { useState, useEffect } from 'react'
import styles from './CommunityPage.module.scss'
import WelcomeHeader from '../../WelcomePage/components/WelcomeHeader/WelcomeHeader'
import CommunityService from '../../../service/CommunityService'
import Pagination from '../../../components/Pagination/Pagination'
import Loader from '../../../components/Loader/Loader'
import SearchBar from '../MoviePage/components/SearchBar/SearchBar'
import { useAuth } from '../../../contexts/AuthContext'
import AuthModal from '../../../components/AuthModal/AuthModal'
import CreatePostModal from './components/CreatePostModal/CreatePostModal'
import CreateChatModal from './components/CreateChatModal/CreateChatModal'
import { PeopleIcon, StarIcon, HeartIcon, EyeIcon, CommentIcon, EditIcon, PlusIcon, CheckIcon, ClockIcon, DocumentIcon, LockIcon, PinIcon, ForumIcon, FolderIcon, SettingsIcon, GlobeIcon, MicrophoneIcon } from '../../../components/CustomIcons'
import CreateForumModal from './components/CreateForumModal/CreateForumModal'
import CreateThreadModal from './components/CreateThreadModal/CreateThreadModal'
import TextChat from '../../../components/TextChat/TextChat'
import VoiceChat from '../../../components/VoiceChat/VoiceChat'

const CommunityPage = ({ embedded = false, goBack }) => {
  const { isAuthenticated, user } = useAuth()
  const [posts, setPosts] = useState([])
  const [categories, setCategories] = useState([])
  const [featuredPosts, setFeaturedPosts] = useState([])
  const [communityStats, setCommunityStats] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [retryAction, setRetryAction] = useState(null)
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [view, setView] = useState('all') // all, featured, trending
  const [selectedCategory, setSelectedCategory] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState('-created_at')
  
  // Новые состояния для расширенной функциональности
  const [activeTab, setActiveTab] = useState('posts') // posts, chats, forums
  const [chatRooms, setChatRooms] = useState([])
  const [forums, setForums] = useState([])
  const [showCreatePost, setShowCreatePost] = useState(false)
  const [showCreateChat, setShowCreateChat] = useState(false)
  const [showCreateForum, setShowCreateForum] = useState(false)
  const [showCreateThread, setShowCreateThread] = useState(false)
  const [selectedChatRoom, setSelectedChatRoom] = useState(null)
  const [chatMessages, setChatMessages] = useState([])
  const [newMessage, setNewMessage] = useState('')
  const [selectedForum, setSelectedForum] = useState(null)
  const [selectedThread, setSelectedThread] = useState(null)
  const [forumThreads, setForumThreads] = useState([])
  const [threadPosts, setThreadPosts] = useState([])
  const [newPostContent, setNewPostContent] = useState('')

  useEffect(() => {
    // Первоначальная загрузка только при монтировании
    const initializeData = async () => {
      try {
        if (activeTab === 'posts') {
          await loadData()
        } else if (activeTab === 'chats') {
          await loadChatRooms()
        } else if (activeTab === 'forums') {
          await loadForums()
        }
        await loadCategories()
        await loadCommunityStats()
      } catch (error) {
        console.error('Ошибка загрузки данных сообщества:', error)
      }
    }
    initializeData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  // Функция применения фильтров с защитой от множественных вызовов
  const applyFilters = () => {
    if (loading) return // Предотвращаем множественные вызовы
    setCurrentPage(1)
    loadData()
  }

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      if (view === 'all') {
        await loadPosts()
      } else if (view === 'featured') {
        await loadFeaturedPosts()
      } else if (view === 'trending') {
        await loadTrendingPosts()
      }
      
      await loadCategories()
      await loadCommunityStats()
    } catch (err) {
      setError('Ошибка загрузки сообщества')
      setRetryAction(() => loadData)
      console.error('Error loading community data:', err)
    } finally {
      setLoading(false)
    }
  }
  
  const checkAuthAndExecute = (action) => {
    if (!isAuthenticated) {
      setShowAuthModal(true)
      return false
    }
    return action()
  }
  
  const handleRetry = () => {
    if (retryAction) {
      retryAction()
    }
  }

  const loadPosts = async () => {
    const params = {
      page: currentPage,
      search: searchTerm,
      ordering: sortBy
    }
    
    if (selectedCategory) {
      params.category = selectedCategory
    }
    
    const response = await CommunityService.getCommunityPosts(params)
    setPosts(response.data.results || response.data || [])
    setTotalPages(response.data.total_pages || 1)
  }

  const loadFeaturedPosts = async () => {
    const response = await CommunityService.getFeaturedPosts()
    setFeaturedPosts(response.data || [])
  }

  const loadTrendingPosts = async () => {
    const response = await CommunityService.getTrendingPosts()
    setPosts(response.data || [])
  }

  const loadCategories = async () => {
    try {
      const response = await CommunityService.getCommunityCategories()
      setCategories(response.data || [])
    } catch (err) {
      console.error('Error loading categories:', err)
    }
  }

  const loadCommunityStats = async () => {
    try {
      const response = await CommunityService.getCommunityStats()
      setCommunityStats(response.data || {})
    } catch (err) {
      console.error('Error loading stats:', err)
    }
  }

  // Новые функции для чатов и форумов
  const loadChatRooms = async () => {
    try {
      const response = await CommunityService.getChatRooms()
      setChatRooms(response.data || [])
    } catch (err) {
      console.error('Error loading chat rooms:', err)
    }
  }

  const loadForums = async () => {
    try {
      const response = await CommunityService.getForums()
      setForums(response.data || [])
    } catch (err) {
      console.error('Error loading forums:', err)
    }
  }

  const loadChatMessages = async (roomId) => {
    try {
      const response = await CommunityService.getChatMessages(roomId)
      setChatMessages(response.data || [])
    } catch (err) {
      console.error('Error loading chat messages:', err)
    }
  }

  const loadForumThreads = async (forumId) => {
    try {
      const response = await CommunityService.getForumThreads(forumId)
      setForumThreads(response.data || [])
    } catch (err) {
      console.error('Error loading forum threads:', err)
    }
  }

  const handleJoinChatRoom = async (room) => {
    console.log('🚀 Joining chat room:', room);
    checkAuthAndExecute(async () => {
      try {
        await CommunityService.joinChatRoom(room.slug)
        console.log('✅ Successfully joined room, setting selectedChatRoom:', room);
        setSelectedChatRoom(room)
        await loadChatMessages(room.slug)
      } catch (err) {
        console.error('❌ Error joining chat room:', err)
      }
    })
  }

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedChatRoom) return

    checkAuthAndExecute(async () => {
      try {
        const messageData = {
          room: selectedChatRoom.id,
          content: newMessage.trim()
        }
        await CommunityService.sendChatMessage(messageData)
        setNewMessage('')
        await loadChatMessages(selectedChatRoom.slug)
      } catch (err) {
        console.error('Error sending message:', err)
      }
    })
  }

  const handleCreatePost = async (postData) => {
    checkAuthAndExecute(async () => {
      try {
        await CommunityService.createCommunityPost(postData)
        setShowCreatePost(false)
        await loadData()
      } catch (err) {
        console.error('Error creating post:', err)
      }
    })
  }

  const handleCreateChatRoom = async (roomData) => {
    checkAuthAndExecute(async () => {
      try {
        // Generate slug from name
        const slug = roomData.name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '')
        
        await CommunityService.createChatRoom({ ...roomData, slug })
        setShowCreateChat(false)
        await loadChatRooms()
      } catch (err) {
        console.error('Error creating chat room:', err)
      }
    })
  }

  const handleCreateForum = async (forumData) => {
    checkAuthAndExecute(async () => {
      try {
        await CommunityService.createForum(forumData)
        setShowCreateForum(false)
        await loadForums()
      } catch (err) {
        console.error('Error creating forum:', err)
      }
    })
  }

  const handleCreateThread = async (threadData) => {
    checkAuthAndExecute(async () => {
      try {
        if (!selectedForum) return
        await CommunityService.createForumThread(selectedForum.id, threadData)
        setShowCreateThread(false)
        await loadForumThreads(selectedForum.id)
      } catch (err) {
        console.error('Error creating thread:', err)
      }
    })
  }

  const handleCreateForumPost = async (postContent) => {
    checkAuthAndExecute(async () => {
      try {
        if (!selectedThread) return
        await CommunityService.createForumPost(selectedThread.id, { content: postContent })
        setNewPostContent('')
        await loadThreadPosts(selectedThread.id)
      } catch (err) {
        console.error('Error creating forum post:', err)
      }
    })
  }

  const loadThreadPosts = async (threadId) => {
    try {
      const response = await CommunityService.getForumPosts(threadId)
      setThreadPosts(response.data || [])
    } catch (err) {
      console.error('Error loading thread posts:', err)
    }
  }

  const handleTabChange = (tab) => {
    setActiveTab(tab)
    setCurrentPage(1)
    
    if (tab === 'chats' && chatRooms.length === 0) {
      loadChatRooms()
    } else if (tab === 'forums' && forums.length === 0) {
      loadForums()
    }
  }

  const handleLikePost = async (post) => {
    checkAuthAndExecute(async () => {
      try {
        await CommunityService.likeCommunityPost(post.slug)
        await loadData()
      } catch (err) {
        console.error('Error liking post:', err)
      }
    })
  }

  const handleSearch = (searchTerm) => {
    setSearchTerm(searchTerm)
    // Обновляем только по кнопке поиска или применения фильтров
  }

  const handleCategoryChange = (category) => {
    setSelectedCategory(category)
    // Обновляем только по кнопке применения фильтров
  }

  if (loading && posts.length === 0 && featuredPosts.length === 0) {
    return (
      <div className={styles.loadingContainer}>
        <Loader size="large" />
        <p>Загрузка сообщества...</p>
      </div>
    )
  }

  const currentPosts = view === 'featured' ? featuredPosts : posts

  return (
    <div className={styles.communityPage}>
      {!embedded && <WelcomeHeader />}
      
      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>
            <span className={styles.icon}><PeopleIcon size={32} /></span>
            Сообщество
          </h1>
          <p className={styles.subtitle}>
            Общение, обсуждения и новости игрового сообщества
          </p>
        </div>

        {/* Статистика */}
        {communityStats && Object.keys(communityStats).length > 0 && (
          <div className={styles.statsCards}>
            <div className={styles.statCard}>
              <div className={styles.statValue}>{communityStats.total_posts || 0}</div>
              <div className={styles.statLabel}>Постов</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statValue}>{communityStats.total_comments || 0}</div>
              <div className={styles.statLabel}>Комментариев</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statValue}>{communityStats.total_users || 0}</div>
              <div className={styles.statLabel}>Участников</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statValue}>{communityStats.active_chat_rooms || 0}</div>
              <div className={styles.statLabel}>Активных чатов</div>
            </div>
          </div>
        )}

        {/* Навигация */}
        <div className={styles.navigation}>
          <div className={styles.tabs}>
            <button 
              className={`${styles.tab} ${activeTab === 'posts' ? styles.active : ''}`}
              onClick={() => handleTabChange('posts')}
            >
              Посты
            </button>
            <button 
              className={`${styles.tab} ${activeTab === 'chats' ? styles.active : ''}`}
              onClick={() => handleTabChange('chats')}
            >
              Чаты
            </button>
            <button 
              className={`${styles.tab} ${activeTab === 'forums' ? styles.active : ''}`}
              onClick={() => handleTabChange('forums')}
            >
              Форумы
            </button>
          </div>
          
          {isAuthenticated && (
            <div className={styles.createButtons}>
              {activeTab === 'posts' && (
                <button 
                  className={styles.createButton}
                  onClick={() => setShowCreatePost(true)}
                >
                  <EditIcon size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />Создать пост
                </button>
              )}
              {activeTab === 'chats' && (
                <button 
                  className={styles.createButton}
                  onClick={() => setShowCreateChat(true)}
                >
                  <PlusIcon size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />Создать чат
                </button>
              )}
              {activeTab === 'forums' && (
                <button 
                  className={styles.createButton}
                  onClick={() => setShowCreateForum(true)}
                >
                  <PlusIcon size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />Создать форум
                </button>
              )}
            </div>
          )}
        </div>

        {/* Фильтры и поиск */}
        <div className={styles.communityFilters}>
          <div className={styles.filterGroup}>
            <label>Поиск</label>
            <SearchBar 
              onSearch={handleSearch}
              placeholder="Поиск в сообществе..."
            />
          </div>
          
          <div className={styles.filterRow}>
            <div className={styles.filterGroup}>
              <label>Категория</label>
              <select 
                value={selectedCategory} 
                onChange={(e) => handleCategoryChange(e.target.value)}
                className={styles.filterSelect}
              >
                <option value="">Все категории</option>
                {categories.map(category => (
                  <option key={category.id} value={category.slug}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className={styles.filterGroup}>
              <label>Сортировка</label>
              <select 
                value={sortBy} 
                onChange={(e) => {
                  setSortBy(e.target.value)
                  // Убираем автоматическое обновление - теперь только по кнопке
                }}
                className={styles.filterSelect}
              >
                <option value="-created_at">Новые сверху</option>
                <option value="created_at">Старые сверху</option>
                <option value="-likes_count">По популярности</option>
                <option value="-comments_count">По комментариям</option>
                <option value="-views_count">По просмотрам</option>
                <option value="title">По названию</option>
              </select>
            </div>
          </div>
        </div>

        {error && (
          <div className={styles.errorContainer}>
            <div className={styles.errorIcon}><PeopleIcon size={64} /></div>
            <div className={styles.errorContent}>
              <h3 className={styles.errorTitle}>Не удалось загрузить сообщество</h3>
              <p className={styles.errorMessage}>{error}</p>
              <button 
                onClick={handleRetry}
                className={styles.retryButton}
                disabled={loading}
              >
                {loading ? 'Загрузка...' : 'Повторить попытку'}
              </button>
            </div>
          </div>
        )}

        {!isAuthenticated && (
          <div className={styles.authPrompt}>
            <div className={styles.authPromptIcon}><LockIcon size={48} /></div>
            <div className={styles.authPromptContent}>
              <h3>Требуется авторизация</h3>
              <p>Для участия в сообществе необходимо войти в систему</p>
              <button 
                onClick={() => setShowAuthModal(true)}
                className={styles.authButton}
              >
                Войти в систему
              </button>
            </div>
          </div>
        )}

        {/* Контент */}
        <div className={styles.content}>
          {activeTab === 'posts' && renderPostsContent()}
          {activeTab === 'chats' && renderChatsContent()}
          {activeTab === 'forums' && renderForumsContent()}
        </div>
      </div>

      {/* Модальное окно авторизации */}
      {showAuthModal && (
        <AuthModal 
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          onSuccess={() => {
            setShowAuthModal(false)
            loadData() // Перезагрузить данные после входа
          }}
        />
      )}

      {/* Модальное окно создания поста */}
      {showCreatePost && (
        <CreatePostModal
          isOpen={showCreatePost}
          onClose={() => setShowCreatePost(false)}
          onSubmit={handleCreatePost}
          categories={categories}
        />
      )}

      {/* Модальное окно создания чата */}
      {showCreateChat && (
        <CreateChatModal
          isOpen={showCreateChat}
          onClose={() => setShowCreateChat(false)}
          onSubmit={handleCreateChatRoom}
          categories={categories}
        />
      )}

      {/* Модальное окно создания форума */}
      {showCreateForum && (
        <CreateForumModal
          isOpen={showCreateForum}
          onClose={() => setShowCreateForum(false)}
          onSubmit={handleCreateForum}
          categories={categories}
        />
      )}

      {/* Модальное окно создания темы */}
      {showCreateThread && (
        <CreateThreadModal
          isOpen={showCreateThread}
          onClose={() => setShowCreateThread(false)}
          onSubmit={handleCreateThread}
          forumName={selectedForum?.name}
        />
      )}
    </div>
  )

  // Функции рендеринга контента для разных вкладок
  function renderPostsContent() {
    const currentPosts = view === 'featured' ? featuredPosts : posts

    return (
      <>
        <div className={styles.navigation}>
          <div className={styles.tabs}>
            <button 
              className={`${styles.tab} ${view === 'all' ? styles.active : ''}`}
              onClick={() => {
                setView('all')
                loadData()
              }}
            >
              Все посты
            </button>
            <button 
              className={`${styles.tab} ${view === 'featured' ? styles.active : ''}`}
              onClick={() => {
                setView('featured')
                loadFeaturedPosts()
              }}
            >
              Рекомендуемые
            </button>
            <button 
              className={`${styles.tab} ${view === 'trending' ? styles.active : ''}`}
              onClick={() => {
                setView('trending')
                loadTrendingPosts()
              }}
            >
              В тренде
            </button>
          </div>
        </div>

        {currentPosts.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}><EditIcon size={64} /></div>
            <h3>Постов не найдено</h3>
            <p>Пока что нет ни одного поста в сообществе</p>
          </div>
        ) : (
          <div className={styles.postsGrid}>
            {currentPosts.map((post) => {
              const authorAvatarUrl = post.author?.avatar 
                ? (post.author.avatar.startsWith('http') ? post.author.avatar : `https://security.ncore.kg${post.author.avatar}`)
                : `https://ui-avatars.com/api/?name=${encodeURIComponent(post.author?.username || 'User')}&background=E91E63&color=fff&bold=true`
              
              return (
              <div key={post.id} className={styles.postCard}>
                <div className={styles.postHeader}>
                  <div className={styles.authorInfo}>
                    <img 
                      src={authorAvatarUrl} 
                      alt={post.author?.username} 
                      className={styles.authorAvatar}
                      onError={(e) => {
                        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(post.author?.username || 'User')}&background=E91E63&color=fff&bold=true`
                      }}
                    />
                    <div className={styles.authorDetails}>
                      <h4 className={styles.authorName}>{post.author?.username}</h4>
                      <span className={styles.postDate}>
                        {new Date(post.created_at).toLocaleDateString('ru-RU')}
                      </span>
                    </div>
                  </div>
                  
                  <div className={styles.postMeta}>
                    <span className={styles.category}>{post.category_name}</span>
                    {post.is_pinned && (
                      <span className={styles.pinnedBadge}><PinIcon size={14} style={{ marginRight: '4px' }} /> Закреплено</span>
                    )}
                    {post.is_featured && (
                      <span className={styles.featuredBadge}><StarIcon size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />Рекомендуемый</span>
                    )}
                  </div>
                </div>
                
                <div className={styles.postContent}>
                  <h3 className={styles.postTitle}>{post.title}</h3>
                  <p className={styles.postText}>{post.content}</p>
                  
                  {post.image && (
                    <img 
                      src={post.image} 
                      alt={post.title}
                      className={styles.postImage}
                    />
                  )}
                  
                  {post.video_url && (
                    <div className={styles.videoContainer}>
                      <iframe 
                        src={post.video_url} 
                        title={post.title}
                        frameBorder="0"
                        allowFullScreen
                      />
                    </div>
                  )}
                  
                  {post.external_link && (
                    <a 
                      href={post.external_link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className={styles.externalLink}
                    >
                      🔗 Перейти по ссылке
                    </a>
                  )}
                </div>

                <div className={styles.postActions}>
                  <button 
                    className={`${styles.actionButton} ${post.is_liked ? styles.liked : ''}`}
                    onClick={() => handleLikePost(post)}
                  >
                    <HeartIcon size={16} style={{ verticalAlign: 'middle', marginRight: '4px', fill: post.is_liked ? '#ff5078' : '#888' }} />{post.likes_count || 0}
                  </button>
                  <button className={styles.actionButton}>
                    <CommentIcon size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{post.comments_count || 0}
                  </button>
                  <button className={styles.actionButton}>
                    <EyeIcon size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{post.views_count || 0}
                  </button>
                </div>

                {post.recent_comments && post.recent_comments.length > 0 && (
                  <div className={styles.recentComments}>
                    <h4>Последние комментарии:</h4>
                    {post.recent_comments.map((comment) => (
                      <div key={comment.id} className={styles.commentPreview}>
                        <strong>{comment.author?.username}:</strong> {comment.content}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )})}
          </div>
        )}

        {loading && (
          <div className={styles.loadingMore}>
            <Loader size="medium" />
          </div>
        )}

        {totalPages > 1 && view === 'all' && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => {
              setCurrentPage(page)
              if (page !== currentPage) {
                loadData()
              }
            }}
          />
        )}
      </>
    )
  }

  function renderChatsContent() {
    console.log('renderChatsContent - selectedChatRoom:', selectedChatRoom);
    console.log('renderChatsContent - user:', user);
    
    return (
      <div className={styles.chatsContainer}>
        {selectedChatRoom ? (
          <div className={styles.chatRoom}>
            <div className={styles.chatHeader}>
              <button 
                className={styles.backButton}
                onClick={() => setSelectedChatRoom(null)}
              >
                ← Назад к чатам
              </button>
              <div className={styles.roomInfo}>
                <h3>{selectedChatRoom.name}</h3>
                <span className={styles.roomDescription}>{selectedChatRoom.description}</span>
              </div>
              <span className={styles.membersCount}>
                <PeopleIcon size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{selectedChatRoom.members_count || 0} участников
              </span>
            </div>
            
            <div className={styles.chatContent}>
              {/* Голосовой чат для voice-комнат */}
              {selectedChatRoom.room_type === 'voice' && (
                <div className={styles.voiceChatSection}>
                  <VoiceChat 
                    roomSlug={selectedChatRoom.slug}
                    roomName={selectedChatRoom.name}
                  />
                </div>
              )}
              
              {/* Текстовый чат для всех типов комнат */}
              <div className={styles.textChatSection}>
                <TextChat 
                  roomSlug={selectedChatRoom.slug}
                  currentUser={user}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.chatRoomsGrid}>
            {chatRooms.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}><CommentIcon size={64} /></div>
                <h3>Чатов не найдено</h3>
                <p>Пока что нет ни одного чата в сообществе</p>
                {isAuthenticated && (
                  <button 
                    className={styles.createButton}
                    onClick={() => setShowCreateChat(true)}
                  >
                    <PlusIcon size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />Создать первый чат
                  </button>
                )}
              </div>
            ) : (
              chatRooms.map((room) => (
                <div 
                  key={room.id} 
                  className={`${styles.chatRoomCard} ${styles[room.room_type]}`}
                >
                  <div className={styles.chatRoomHeader}>
                    <span className={styles.roomIcon}>
                      {room.room_type === 'public' ? <GlobeIcon size={24} /> : 
                       room.room_type === 'private' ? <LockIcon size={24} /> : 
                       <MicrophoneIcon size={24} />}
                    </span>
                    <div className={styles.roomInfo}>
                      <h4>{room.name}</h4>
                      <div className={styles.roomBadges}>
                        <span className={`${styles.roomType} ${styles[room.room_type]}`}>
                          {room.room_type === 'public' ? 'Публичный' : 
                           room.room_type === 'private' ? 'Приватный' : 
                           'Голосовой'}
                        </span>
                        {room.is_active && (
                          <span className={styles.activeBadge}>Активен</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <p className={styles.chatRoomDescription}>{room.description}</p>
                  <div className={styles.chatRoomMeta}>
                    <span><PeopleIcon size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{room.members_count || 0} участников</span>
                    <span>📂 {room.category_name}</span>
                    {room.last_message_at && (
                      <span><CommentIcon size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{new Date(room.last_message_at).toLocaleString('ru-RU')}</span>
                    )}
                  </div>
                  <div className={styles.chatRoomActions}>
                    <button
                      className={styles.joinButton}
                      onClick={() => handleJoinChatRoom(room)}
                    >
                      {room.room_type === 'voice' ? (
                        <>
                          <MicrophoneIcon size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                          Подключиться
                        </>
                      ) : 'Присоединиться'}
                    </button>
                    {room.owner?.id === user?.id && (
                      <button className={styles.manageButton}>
                        <SettingsIcon size={16} style={{ marginRight: '4px' }} /> Управление
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    )
  }

  function renderForumsContent() {
    // Просмотр постов в теме
    if (selectedThread) {
      return (
        <div className={styles.threadView}>
          <div className={styles.threadViewHeader}>
            <button 
              className={styles.backButton}
              onClick={() => {
                setSelectedThread(null)
                setThreadPosts([])
              }}
            >
              ← Назад к темам
            </button>
            <div className={styles.threadInfo}>
              <h3>{selectedThread.title}</h3>
              <div className={styles.threadMeta}>
                <span>👤 Автор: {selectedThread.user?.username}</span>
                <span><CommentIcon size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{threadPosts.length} ответов</span>
                <span><EyeIcon size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{selectedThread.views_count} просмотров</span>
                <span>🕒 {new Date(selectedThread.created_at).toLocaleDateString('ru-RU')}</span>
              </div>
            </div>
          </div>

          {/* Голосовое обсуждение для темы форума */}
          <div className={styles.threadVoiceChat}>
            <VoiceChat 
              roomSlug={selectedThread.slug || `thread-${selectedThread.id}`}
              roomName={`Голосовое обсуждение: ${selectedThread.title}`}
            />
          </div>

          <div className={styles.postsList}>
            {threadPosts.map((post, index) => {
              const avatarUrl = post.user?.avatar 
                ? (post.user.avatar.startsWith('http') ? post.user.avatar : `https://security.ncore.kg${post.user.avatar}`)
                : `https://ui-avatars.com/api/?name=${encodeURIComponent(post.user?.username || 'User')}&background=E91E63&color=fff&bold=true`
              
              const formatDate = (dateString) => {
                if (!dateString) return 'Недавно'
                const date = new Date(dateString)
                return isNaN(date.getTime()) ? 'Недавно' : date.toLocaleString('ru-RU')
              }
              
              return (
              <div key={post.id} className={styles.postCard}>
                <div className={styles.postAuthor}>
                  <img 
                    src={avatarUrl} 
                    alt={post.user?.username}
                    className={styles.avatarLarge}
                    onError={(e) => {
                      e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(post.user?.username || 'User')}&background=E91E63&color=fff&bold=true`
                    }}
                  />
                  <div className={styles.authorInfo}>
                    <strong>{post.user?.username}</strong>
                    <span className={styles.postDate}>
                      {formatDate(post.created_at)}
                    </span>
                    {index === 0 && <span className={styles.opBadge}>Автор темы</span>}
                    {post.is_solution && <span className={styles.solutionBadge}>✓ Решение</span>}
                  </div>
                </div>
                <div className={styles.postContent}>
                  {post.content}
                  {post.is_edited && (
                    <span className={styles.editedLabel}>
                      (отредактировано {formatDate(post.edited_at)})
                    </span>
                  )}
                </div>
              </div>
            )})}
          </div>

          {isAuthenticated && !selectedThread.is_locked && (
            <div className={styles.replyForm}>
              <h4>
                <CommentIcon size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                Ответить в теме
              </h4>
              <textarea
                value={newPostContent}
                onChange={(e) => setNewPostContent(e.target.value)}
                placeholder="Напишите ваш ответ..."
                rows={5}
                className={styles.replyTextarea}
              />
              <button 
                onClick={() => handleCreateForumPost(newPostContent)}
                className={styles.replyButton}
                disabled={!newPostContent.trim()}
              >
                Отправить ответ
              </button>
            </div>
          )}

          {selectedThread.is_locked && (
            <div className={styles.lockedNotice}>
              <LockIcon size={18} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
              Эта тема заблокирована и недоступна для новых ответов
            </div>
          )}
        </div>
      )
    }

    // Список тем в форуме
    if (selectedForum) {
      return (
        <div className={styles.forumView}>
          <div className={styles.forumViewHeader}>
            <button 
              className={styles.backButton}
              onClick={() => {
                setSelectedForum(null)
                setForumThreads([])
              }}
            >
              ← Назад к форумам
            </button>
            <div className={styles.forumInfo}>
              <h3><ForumIcon size={24} style={{ marginRight: '8px' }} /> {selectedForum.name}</h3>
              <p>{selectedForum.description}</p>
            </div>
            {isAuthenticated && (
              <button
                className={styles.createButton}
                onClick={() => setShowCreateThread(true)}
              >
                📝 Создать тему
              </button>
            )}
          </div>
          
          <div className={styles.threadsList}>
            {forumThreads.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>📄</div>
                <h3>Тем не найдено</h3>
                <p>В этом форуме пока нет тем для обсуждения</p>
                {isAuthenticated && (
                  <button
                    className={styles.createButton}
                    onClick={() => setShowCreateThread(true)}
                  >
                    📝 Создать первую тему
                  </button>
                )}
              </div>
            ) : (
              forumThreads.map((thread) => (
                <div 
                  key={thread.id} 
                  className={`${styles.threadCard} ${thread.is_pinned ? styles.pinned : ''}`}
                  onClick={() => {
                    setSelectedThread(thread)
                    loadThreadPosts(thread.id)
                  }}
                >
                  <div className={styles.threadHeader}>
                    <h4>{thread.title}</h4>
                    <div className={styles.threadBadges}>
                      {thread.is_pinned && <span className={styles.pinnedBadge}><PinIcon size={14} style={{ marginRight: '4px' }} /> Закреплено</span>}
                      {thread.is_locked && <span className={styles.lockedBadge}><LockIcon size={14} style={{ marginRight: '4px' }} /> Заблокировано</span>}
                      {thread.is_solved && <span className={styles.solvedBadge}>✓ Решено</span>}
                    </div>
                  </div>
                  <div className={styles.threadMeta}>
                    <span>👤 {thread.user?.username || 'Неизвестно'}</span>
                    <span><CommentIcon size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{thread.posts_count || 0} ответов</span>
                    <span><EyeIcon size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{thread.views_count} просмотров</span>
                    <span>🕒 {new Date(thread.created_at).toLocaleDateString('ru-RU')}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )
    }

    // Список форумов
    return (
      <div className={styles.forumsContainer}>
        <div className={styles.forumsGrid}>
          {forums.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}><ForumIcon size={48} /></div>
              <h3>Форумов не найдено</h3>
              <p>Пока что нет ни одного форума в сообществе</p>
              {isAuthenticated && (
                <button
                  className={styles.createButton}
                  onClick={() => setShowCreateForum(true)}
                >
                  <ForumIcon size={18} style={{ marginRight: '6px' }} /> Создать первый форум
                </button>
              )}
            </div>
          ) : (
            forums.map((forum) => (
              <div key={forum.id} className={styles.forumCard}>
                <div className={styles.forumCardHeader}>
                  <div className={styles.forumIcon}><ForumIcon size={32} /></div>
                  <div className={styles.forumCardInfo}>
                    <h4>{forum.name}</h4>
                    <span className={styles.categoryBadge}>{forum.category_name}</span>
                    {forum.is_private && <span className={styles.privateBadge}><LockIcon size={14} style={{ marginRight: '4px' }} /> Приватный</span>}
                  </div>
                </div>
                <p className={styles.forumDescription}>{forum.description}</p>
                <div className={styles.forumStats}>
                  <span>📄 {forum.threads_count || 0} тем</span>
                  <span><CommentIcon size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{forum.posts_count || 0} сообщений</span>
                </div>
                <button 
                  className={styles.enterButton}
                  onClick={() => {
                    setSelectedForum(forum)
                    loadForumThreads(forum.id)
                  }}
                >
                  Войти в форум →
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    )
  }
}

export default CommunityPage